<template lang="pug">
  #map-container
    MglMap(
      container="mapbox-challenge-map"
      :access-token="accessToken"
      :map-style="mapStyle"
      :center="center()"
      @load="onMapLoaded($event)"
    )
      MglGeojsonLayer(
        source-id="challengeLine"
        :layer-id="challengeLineLayer.id"
        :layer="challengeLineLayer"
      )
      MglMarker(
        :coordinates="pointCoordinatesAlongLine(0)"
        :offset="[10,-5]"
      )
        template(slot="marker")
          BIcon(icon="flag-variant" type="is-special-success" size="is-large")
      MglMarker(
        id="marker-finish-line"
        :coordinates="finishLineCoordinates()"
        :offset="[10,-5]"
        @added="finishLineMarkerAdded"
        @click.native="flyToMarker"
      )
        MglPopup(
          @open="e => panToFitPopup(e)"
          @close=""
        )
          .map-finished-entities-list-popup
            .title
              BIcon(icon="flag-checkered" type="is-danger")
              span Finish Line:
            article.media(
              v-for="entity in finishedEntities"
            )
              EntityAvatar.media-left(
                sizeClass="is-48x48"
                :image="entity.avatar"
              )
              .media-content
                .content.has-text-centered
                  .is-size-5.has-text-weight-semibold {{ entityName(entity) }}
                  .has-text-weight-semibold {{ formatWithCommas(entity.steps) }} steps
                  template(v-if="!isPvsPChallenge")
                    RouterLink.button.is-text.is-fullwidth.is-small(
                      v-if="isTeamChallenge"
                      :to="{ name: 'Team', params: { teamId: entity.teamNameURL } }"
                    ) Visit Team
                    RouterLink.button.is-text.is-fullwidth.is-small(
                      v-else
                      :to="{ name: 'ProfilePage', params: { username: entity.username } }"
                    ) Visit Profile
            .is-size-5(v-if="finishedEntitiesCount === 0") No {{ entityType }} have reached the finish line.
              .is-size-6 Step by step...
        template(slot="marker")
          .level
            .level-right
              .level-item
                BIcon(icon="flag-checkered" type="is-danger" size="is-large")
                .tag.is-danger.is-rounded(v-if="finishedEntitiesCount > 0") {{ finishedEntitiesCount }}

      // Entity Markers
      MglMarker(
        v-for="(entity, index) in notfinishedEntities"
        :key="`marker-${entityName(entity)}`"
        :id="`marker-${entityName(entity)}`"
        :ref="`marker-${entityName(entity)}`"
        @added="avatarMarkerAdded"
        @click.native="flyToMarker"
        :coordinates="entityCoordinates(entity.steps)"
      )
        MglPopup(
          @close=""
        )
          .container.entity-marker-popup
            .title.popup-username(:id="`popup-${entityName(entity)}`") {{ entityName(entity) }}
            .subtitle Progress:&nbsp;
              span.has-text-weight-semibold {{ formatWithCommas(entity.steps) }} steps
            template(v-if="!isPvsPChallenge")
              RouterLink.button.is-outlined.is-fullwidth.is-primary.is-rounded(
                v-if="isTeamChallenge"
                :to="{ name: 'Team', params: { teamId: entity.teamNameURL } }"
              ) Visit Team
              RouterLink.button.is-outlined.is-fullwidth.is-primary.is-rounded(
                v-else
                :to="{ name: 'ProfilePage', params: { username: entity.username } }"
              ) Visit Profile
        EntityAvatar(
          slot="marker"
          map-marker
          :index="index"
          :image="entity.avatar"
        )
      // Pacer Rabbit Marker
      MglMarker(
        v-if="pacerValue"
        :key="`marker-pacer-rabbit`"
        :id="`marker-pacer-rabbit`"
        @added="avatarMarkerAdded"
        @click.native="flyToMarker"
        :coordinates="entityCoordinates(pacerValue)"
      )
        MglPopup(
          @close=""
        )
          .container.entity-marker-popup
            .title.popup-username(:id="`popup-pacer-rabbit`") Pacer Rabbit
            .subtitle Progress:&nbsp;
              span.has-text-weight-semibold {{ formatWithCommas(pacerValue) }} steps
            p.is-size-6(style="max-width:260px")
              | The Pacer Rabbit shows where a challenge member would be if they meet the daily step goal.
              | Use the Pacer Rabbit to determine if you are on track, falling behind, or ahead of the challenge average!
        EntityAvatar(
          slot="marker"
          map-marker
          :index="-1"
          image="https://walkertracker.s3.amazonaws.com/stride/icon_pacer_rabbit.svg"
        )
      // Milestone Markers
      MglMarker(
        v-for="(milestone, index) in milestones"
        :key="`milestone-${milestone.id}`"
        :id="`milestone-${milestone.id}`"
        :ref="`milestone-${milestone.id}`"
        :coordinates="pointCoordinatesAlongLine(milestone.steps/challenge.multiplier)"
        @added="milestoneMarkerAdded"
        @click.native="flyToMarker"
      )
        // color purple
        .milestone-marker(
          slot="marker"
          :style="{ color: `#9E5195` }"
        )
          BIcon(icon="map-marker" size="is-medium")
        MglPopup(
          @open="e => panToFitPopup(e)"
          @close=""
          :closeButton="false"
        )
          .milestone-popup
            WtIconButton.close-button(
              @click.native="closePopups(milestoneMarkers)"
              aria-label="Close Milestone"
              style="position: fixed; top: 1rem; right: 1rem"
            )
              BIcon(icon="close-circle" type="is-grey-light" size="is-medium")
            section.content
              .title {{ milestone.title }}
              .sub-title
                span.is-size-4 {{ formatWithCommas(Math.round(milestone.steps / challenge.multiplier)) }} steps
                span.is-size-4 &nbsp;milestone

            .columns
              .column.is-half-desktop(v-if="milestone.imageLarge || milestone.video")
                section.content(v-if="milestone.imageLarge")
                  figure.milestone-image(:data-src="milestone.imageLarge")
                    img.image(
                      :src="milestone.imageLarge"
                      alt="milestone image"
                      width="auto"
                      height="40%"
                      @error="milestoneImageError"
                    )
                section.content(v-if="milestone.video")
                  .milestone-video(:data-src="milestone.video")
                    iframe(
                      width="100%"
                      height="auto"
                      frameborder="0"
                      :src="milestone.video"
                      alt="milestone video"
                      allowfullscreen
                    )
              .column
                section.content
                  .milestone-description.mb-3 {{ milestone.description }}
                  a.has-text-info.milestone-more-info-link(
                    v-if="milestone.moreInfoLink"
                    :href="milestone.moreInfoLink"
                    target="_blank"
                  ) More information

</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import Mapbox from "mapbox-gl";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import { MglMap, MglGeojsonLayer, MglMarker, MglPopup } from "vue-mapbox";
import { lineString } from "@turf/helpers";
import along from "@turf/along";
import centerOfMass from "@turf/center-of-mass";
import EntityListDropdown from "@@/Challenge/MapChallenge/ChallengeMapEntityListDropdown";
import EntityAvatar from "@@/WtEntityAvatar/WtEntityAvatar";
import WtIconButton from "@@/WtIconButton/WtIconButton";

export default {
  name: "ChallengeMap",

  components: {
    Mapbox,
    MglMap,
    MglPopup,
    MglGeojsonLayer,
    MglMarker,
    EntityListDropdown,
    EntityAvatar,
    WtIconButton
  },

  mixins: [numberUtilityMixin],

  props: {
    lineCoordinates: {
      type: Array,
      required: true
    },

    leaderboard: {
      type: Array,
      required: true
    },

    milestones: {
      type: Array,
      required: true
    },

    challenge: {
      type: Object,
      required: true
    },

    pacerValue: {
      type: Number,
      default: undefined
    },

    totalParticipantsMetGoal: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      avatarMarkers: {},
      milestoneMarkers: {},
      finishLineMarker: {},
      challengeLoaded: false,
      mapStyle: "mapbox://styles/mapbox/streets-v11",
      accessToken: process.env.VUE_APP_MAP_BOX_ACCESS_TOKEN,
      flying: true,
      doneFlyToAuthMember: false
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberLoaded"]),

    isTeamChallenge() {
      return this.challenge.between === "groups" && !this.isPvsPChallenge;
    },

    isPvsPChallenge() {
      return this.challenge.isPvsPChallenge;
    },

    entityType() {
      if (this.isPvsPChallenge) {
        return "programs";
      }
      if (this.isTeamChallenge) {
        return "teams";
      }
      return "members";
    },

    authMemberIsInChallenge() {
      return !this._.isEmpty(
        this.$auth
          .user()
          .challenges.find(challenge => challenge.id === this.challenge.id)
      );
    },

    /**
     * returns an object per the customLayerInterface, which is passed
     * to the MglGeoJsonLayer component to draw the line
     * https://docs.mapbox.com/mapbox-gl-js/api/#customlayerinterface
     */
    challengeLineLayer() {
      return {
        id: "route",
        type: "line",
        source: this.challengeLineSource(),
        layout: {
          "line-join": "round",
          "line-cap": "round"
        },
        paint: {
          "line-color": "#888",
          "line-width": 8
        }
      };
    },

    finishedEntitiesCount() {
      return this.totalParticipantsMetGoal;
    },

    finishedEntities() {
      return this.leaderboard.reduce((acc, curr) => {
        if (
          curr.steps * this.challenge.multiplier >=
          this.challenge.totalStepsDistance
        ) {
          // note, append curr so steps stayed in ascending order
          return [...acc, curr];
        }
        return acc;
      }, []);
    },

    notfinishedEntities() {
      return this.leaderboard.reduce((acc, curr) => {
        if (
          curr.steps * this.challenge.multiplier <
          this.challenge.totalStepsDistance
        ) {
          // note, append curr so steps stayed in ascending order
          return [...acc, curr];
        }
        return acc;
      }, []);
    },

    authMemberDistanceUnit() {
      return this.memberLoaded
        ? this._.get(this.member, "memberData.units", "Miles")
        : this._.get(this.$auth.user(), "memberData.units", "Miles");
    },

    entityMarkersLoaded() {
      const entityMarkers = {
        ...this.avatarMarkers,
        ...this.finishLineMarker
      };
      return (
        Object.keys(entityMarkers).length ===
        this.notfinishedEntities.length + 1
      );
    },

    readyToFlyToAuthEntity() {
      return (
        this.entityMarkersLoaded && !this.flying && !this.doneFlyToAuthMember
      );
    }
  },

  watch: {
    $route(to) {
      if (to.params.milestoneId) {
        this.flyToMarker({
          target: { id: "milestone-" + to.params.milestoneId }
        });
      }
    },

    async readyToFlyToAuthEntity(ready) {
      // Fly to the logged in member, or team, after the map loads and all markers are added to the map
      if (ready) {
        await this.$nextTick(); // dom needs to update after markers are added
        this.flyToAuthEntity();
      }
    }
  },

  created() {
    // https://soal.github.io/vue-mapbox/guide/basemap.html#adding-map-component
    // We need to set mapbox-gl library here in order to use it in template
    this.mapbox = Mapbox;
    // https://soal.github.io/vue-mapbox/guide/basemap.html#map-loading
    // If you want to store map object, store it as non-reactive property like in example below.
    this.map = null;
  },

  methods: {
    /**
     * http://turfjs.org/docs/#lineString
     *
     * @returns {Feature<LineString, {name: string}>}
     */
    challengeLineStringFeature() {
      return lineString(this.lineCoordinates, { name: "challengeLine" });
    },

    /**
     * returns a GeoJSON source object, this is used in conjunction
     * with LineString to supply challengeLineLayer() with the
     * values needed to build a layer object
     *
     * @returns {{data: (*|Feature<LineString, *>), type: string}}
     */
    challengeLineSource() {
      return {
        type: "geojson",
        data: this.challengeLineStringFeature()
      };
    },

    center() {
      return centerOfMass(this.challengeLineStringFeature()).geometry
        .coordinates;
    },

    stepsToDistance(steps) {
      // steps * multiplier =  location in steps on map
      // (location in steps on map) / 2000 = location in miles on map
      // Note, 2000 is the generic amount of steps in a mile that WalkerTracker uses
      // Note, 1250 is the generic amount of steps in a kilometer that WalkerTracker uses
      const stepsInUnit = this.authMemberDistanceUnit === "Miles" ? 2000 : 1250;

      return (steps * this.challenge.multiplier) / stepsInUnit;
    },

    /**
     * get point at which the marker will be placed, this is determined
     * by the entities (member or team) total steps in challenge, which is converted
     * to miles then passed to along() to find the point
     * along the line
     *
     * http://turfjs.org/docs/#along
     *
     * @param entitiesTotalStepsInChallenge or milestoneStepsInChallenge = (milestone.steps / challenge.multiplier)
     * @returns {array} ([lat, lng])
     */
    pointCoordinatesAlongLine(stepsInChallenge) {
      const options = { units: this.authMemberDistanceUnit.toLowerCase() };
      const stepsInUnits = this.stepsToDistance(stepsInChallenge);

      return along(this.challengeLineStringFeature(), stepsInUnits, options)
        .geometry.coordinates;
    },

    finishLineCoordinates() {
      // note, totalStepsDistance is $challenge->competition_tracking_number
      return this.pointCoordinatesAlongLine(
        this.challenge.totalStepsDistance / this.challenge.multiplier
      );
    },

    entityCoordinates(entitySteps) {
      return this.pointCoordinatesAlongLine(entitySteps);
    },

    avatarMarkerAdded(e) {
      // https://soal.github.io/vue-mapbox/api/marker.html#events
      // Payload { component: MarkerComponent, map: Map, marker: Marker }
      this.avatarMarkers = {
        ...this.avatarMarkers,
        [e.component.$el.id]: e.marker
      };
      e.map._avatarMarkers = this.avatarMarkers;
    },

    finishLineMarkerAdded(e) {
      // https://soal.github.io/vue-mapbox/api/marker.html#events
      // Payload { component: MarkerComponent, map: Map, marker: Marker }
      this.finishLineMarker = {
        ...this.finishLineMarker,
        [e.component.$el.id]: e.marker
      };
      e.map._finishLineMarker = this.finishLineMarker;
    },

    flyToMarker(e) {
      const allMarkers = {
        ...this.avatarMarkers,
        ...this.milestoneMarkers,
        ...this.finishLineMarker
      };

      this.closePopups(allMarkers);

      const markerClicked = allMarkers[e.target.id];

      if (markerClicked) {
        markerClicked._map.flyTo({
          center: markerClicked._lngLat,
          speed: 3
        });

        markerClicked.togglePopup();
      }
    },

    milestoneMarkerAdded(e) {
      // https://soal.github.io/vue-mapbox/api/marker.html#events
      // Payload { component: MarkerComponent, map: Map, marker: Marker }
      this.milestoneMarkers = {
        ...this.milestoneMarkers,
        [e.component.$el.id]: e.marker
      };
      e.map._milestoneMarkers = this.milestoneMarkers;
    },

    milestoneImageError(e) {
      e.target.onerror = null;
      e.target.style.display = "none";
    },

    async onMapLoaded(event) {
      this.map = event.map;

      this.map.addControl(new this.mapbox.NavigationControl());
      this.map.addControl(new this.mapbox.FullscreenControl());
      this.map.addControl(this.fitLineControl());
      this.map.addControl(this.flyToEntityControl(), "top-left");

      if (this.$route.params.milestoneId) {
        this.map.once("moveend", () => {
          document
            .getElementById(`milestone-${this.$route.params.milestoneId}`)
            .click();
        });
      }

      this.map.on("zoomstart", () => {
        this.flying = true;
      });
      this.map.on("zoomend", () => {
        this.flying = false;
      });

      this.fitLine();
      // NOTE: we also zoom into the entity when the map is loaded
      // but can only do so when after the markers are added
      // and do so via flyToAuthEntity
    },

    fitLine() {
      const bounds = this.lineCoordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new this.mapbox.LngLatBounds(this.lineCoordinates[0], this.lineCoordinates[0]));

      this.map.fitBounds(bounds, { padding: 30, speed: 2 });
    },

    panToFitPopup(e) {
      // Convert latlng of popup to pixels
      let px = e.map.project([e.popup._lngLat.lng, e.popup._lngLat.lat]);
      // Add pixel height offset to converted pixels
      px.y += e.popup._container.clientHeight / 2;
      // Convert back to coordinates
      const lngLat = e.map.unproject(px);
      // Pan map
      this.map.panTo(lngLat, { animate: true });
    },

    /**
     * wrapper for mapbox custom control
     */
    fitLineControl() {
      const bounds = this.lineCoordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new this.mapbox.LngLatBounds(this.lineCoordinates[0], this.lineCoordinates[0]));

      /**
       * https://docs.mapbox.com/mapbox-gl-js/api/#icontrol
       */
      class fitLineButton {
        onAdd(map) {
          this._map = map;
          this._container = document.createElement("div");
          this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
          this._button = document.createElement("button");
          this._button.id = "fitline-button";
          this._button.textContent = "FIT LINE";
          this._container.appendChild(this._button);

          this._button.onclick = () => {
            this._map.fitBounds(bounds, { padding: 30 });
          };

          return this._container;
        }

        onRemove() {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      }

      return new fitLineButton();
    },

    flyToEntityControl() {
      const leaderboard = this.leaderboard;
      const isTeamChallenge = this.isTeamChallenge;
      const isPvsPChallenge = Boolean(this.isPvsPChallenge);
      const closePopups = this.closePopups;
      /**
       * https://docs.mapbox.com/mapbox-gl-js/api/#icontrol
       */
      class flyToEntityButton {
        onAdd(map) {
          this._map = map;
          this._container = document.createElement("div");
          this._container.id = "map-entity-list-dropdown-container";
          this._container.className = "mapboxgl-ctrl";

          const EntityListDropdownComponentInstance = new Vue({
            ...EntityListDropdown,
            propsData: {
              leaderboard,
              isTeamChallenge,
              isPvsPChallenge
            }
          }).$mount();

          this._container.appendChild(EntityListDropdownComponentInstance.$el);

          const flyToEntityButton = this._container.getElementsByTagName(
            "button"
          )[0];

          if (flyToEntityButton.id === "fly-to-entity-button") {
            flyToEntityButton.onclick = () => {
              closePopups({
                ...this._map._avatarMarkers,
                ...this._map._milestoneMarkers,
                ...this._map._finishLineMarker
              });
            };
          }

          return this._container;
        }

        onRemove() {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      }

      return new flyToEntityButton();
    },

    closePopups(markers) {
      Object.values(markers).forEach(marker => {
        if (marker && marker._popup) {
          marker._popup.remove();
        }
      });
    },

    flyToAuthEntity() {
      let entityName = this.$auth.user().username;
      let markerElement;

      if (this.isTeamChallenge) {
        const teamToFlyTo = this.$auth.user().teams.find(usersTeam => {
          return this.notfinishedEntities.find(
            challengeTeam => challengeTeam.teamName === usersTeam.teamName
          );
        });
        if (!this._.isEmpty(teamToFlyTo)) {
          entityName = teamToFlyTo.teamName;
        } else {
          entityName = null;
        }
      }

      if (this.isPvsPChallenge) {
        entityName = this.$auth.user().program.name;
      }

      if (entityName) {
        markerElement = document.getElementById(`marker-${entityName}`);
      }

      if (this.authMemberIsInChallenge) {
        if (!this._.isEmpty(markerElement)) {
          markerElement.click();
        } else {
          document.getElementById("marker-finish-line").click();
        }
      }
      this.doneFlyToAuthMember = true;
    },

    entityName(entity) {
      if (this.isPvsPChallenge) {
        return entity.programName;
      }
      if (this.isTeamChallenge) {
        return entity.teamName;
      }
      return entity.username;
    }
  }
};
</script>

<style lang="sass" scoped></style>
